import React from "react";
import { PHASES, cohortRows, scoreColor, readinessPct, tierAverage, avgOf } from "@/data/coachingData";
import { useI18n } from "@/i18n";

export function CohortTable({ tiers, scores, headcounts = {} }) {
  const { t } = useI18n();
  const rows = cohortRows(scores, tiers, headcounts);
  const orgAvg = +avgOf(tiers.map((tr) => tierAverage(scores, tr.key))).toFixed(1);
  const phaseShort = (key) => t(`phase.${key}.title`).split(" · ").pop();

  return (
    <div className="overflow-x-auto" data-testid="cohort-table">
      <table className="w-full min-w-[620px] text-sm border-collapse">
        <thead>
          <tr className="border-b border-slate-300">
            <th className="text-left py-2 pr-3 text-slate-600 font-semibold">{t("cohort.level")}</th>
            <th className="text-center py-2 px-2 text-slate-600 font-medium text-xs">{t("cohort.people")}</th>
            {PHASES.map((p) => (
              <th key={p.key} className="text-center py-2 px-2 text-slate-600 font-medium text-xs">
                {phaseShort(p.key)}
              </th>
            ))}
            <th className="text-center py-2 px-2 text-slate-600 font-semibold text-xs">{t("cohort.total")}</th>
            <th className="text-center py-2 px-2 text-slate-600 font-medium text-xs">{t("cohort.readiness")}</th>
            <th className="text-right py-2 pl-2 text-slate-600 font-medium text-xs">{t("cohort.band")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.tier_key} className="border-b border-slate-200" data-testid={`cohort-row-${r.tier_key}`}>
              <td className="py-2.5 pr-3 font-medium text-slate-800 print-text">{t(`tier.${r.tier_key}`)}</td>
              <td className="text-center py-2.5 px-2 font-mono text-slate-600 tabular-nums">{r.headcount}</td>
              {PHASES.map((p) => (
                <td
                  key={p.key}
                  className="text-center py-2.5 px-2 font-mono print-text"
                  style={{ color: scoreColor(r.phases[p.key]) }}
                >
                  {r.phases[p.key].toFixed(1)}
                </td>
              ))}
              <td
                className="text-center py-2.5 px-2 font-mono font-bold print-text"
                style={{ color: scoreColor(r.total) }}
              >
                {r.total.toFixed(1)}
              </td>
              <td className="text-center py-2.5 px-2 font-mono text-slate-700 tabular-nums">{r.readiness}%</td>
              <td className="text-right py-2.5 pl-2">
                <span
                  className="text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap"
                  style={{ background: `${scoreColor(r.total)}1f`, color: scoreColor(r.total) }}
                >
                  {t(`band.${r.band}`)}
                </span>
              </td>
            </tr>
          ))}
          <tr className="border-t-2 border-slate-300">
            <td className="py-2.5 pr-3 font-bold text-slate-900 print-text">{t("cohort.orgRow")}</td>
            <td className="text-center py-2.5 px-2 font-mono text-slate-600 tabular-nums">
              {rows.reduce((a, r) => a + r.headcount, 0)}
            </td>
            {PHASES.map((p) => {
              const v = +avgOf(rows.map((r) => r.phases[p.key])).toFixed(1);
              return (
                <td
                  key={p.key}
                  className="text-center py-2.5 px-2 font-mono font-semibold print-text"
                  style={{ color: scoreColor(v) }}
                >
                  {v.toFixed(1)}
                </td>
              );
            })}
            <td
              className="text-center py-2.5 px-2 font-mono font-bold print-text"
              style={{ color: scoreColor(orgAvg) }}
            >
              {orgAvg.toFixed(1)}
            </td>
            <td className="text-center py-2.5 px-2 font-mono font-semibold text-slate-800 tabular-nums">
              {readinessPct(orgAvg)}%
            </td>
            <td />
          </tr>
        </tbody>
      </table>
    </div>
  );
}
